/**
 * Panel principal del usuario final.
 *
 * Este componente centraliza la experiencia post-login:
 * - perfil y datos base del usuario,
 * - historial clínico,
 * - dieta y rutinas,
 * - posts de acompañamiento,
 * - suscripción a FCM,
 * - y ajustes de cuenta.
 *
 * Puntos técnicos relevantes:
 * - Lee múltiples colecciones de Firestore y archivos de Firebase Storage.
 * - Usa FCM con VAPID key pública.
 * - La cancelación de suscripción consume un backend HTTP externo.
 */
import React, { useCallback, useEffect, useState } from "react";
import "./Dashboard.scss";
import NavbarD from "./NavbarD";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getMessaging, getToken } from "firebase/messaging";

import ClinicalHistoryWizard from "./ClinicalHistoryWizard/ClinicalHistoryWizard";
import Loader from "./Loader";
import { Modal, Button, Calendar, Input } from "antd";
import moment from "moment";
import "moment/locale/es"; // opcional para español
import runtimeConfig from "../config/runtimeConfig";

const Dashboard = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();
  const storage = getStorage();

  const messaging = getMessaging();
  // La VAPID key debe exponerse desde frontend con prefijo REACT_APP_*.
  const VAPID_KEY = runtimeConfig.publicVapidKey;

  const [currentView, setCurrentView] = useState("home");
  const [userData, setUserData] = useState(null);
  const [hasClinicalHistory, setHasClinicalHistory] = useState(false);
  const [clinicalHistoryData, setClinicalHistoryData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [dietPlanUrl, setDietPlanUrl] = useState(null);
  const [routineData, setRoutineData] = useState(null);

  const [editingFields, setEditingFields] = useState({}); // Estado para controlar los campos en edición

  // Funciones para editar campos de medidas
  const handleEdit = (field) => {
    setEditingFields({ ...editingFields, [field]: true });
  };

  const handleBlurUpdate = (field, value) => {
    handleUpdateMeasures(field, value);
    setEditingFields({ ...editingFields, [field]: false });
  };

  // Estado para modal de video:
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const openVideoModal = (videoUrl) => {
    setSelectedVideoUrl(videoUrl);
    setIsVideoModalVisible(true);
  };
  const closeVideoModal = () => {
    setIsVideoModalVisible(false);
    setSelectedVideoUrl(null);
  };

  const [selectedDay, setSelectedDay] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Mapeo de días (sin acentos)
  const daysMap = {
    lunes: "lunes",
    martes: "martes",
    miercoles: "miercoles",
    jueves: "jueves",
    viernes: "viernes",
    sabado: "sabado",
    domingo: "domingo",
  };

  // Para posts de acompañamiento:
  const [posts, setPosts] = useState([]);

  // Guarda el token FCM por usuario para que admin pueda enviar pushes segmentados.
  const subscribeToNotifications = useCallback(async (uid, displayName) => {
    try {
      const status = await Notification.requestPermission();
      if (status === "granted") {
        const fcmToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          forceRefresh: true,
        });
        if (fcmToken) {
          console.log("Token de notificaciones:", fcmToken);
          await setDoc(doc(db, "suscripciones-fcm", uid), {
            uid,
            token: fcmToken,
            nombre: displayName ?? "Usuario",
            fecha: serverTimestamp(),
          });
          console.log("Token FCM guardado.");
        } else {
          console.log("No se pudo obtener token FCM.");
        }
      } else {
        console.log("Permiso de notificaciones denegado.");
      }
    } catch (error) {
      console.error("Error al suscribir a notificaciones:", error);
    }
  }, [VAPID_KEY, db, messaging]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
      } else {
        setIsLoading(true);

        // 1. Datos del usuario
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }

        // 2. Historial Clínico
        const clinicalHistoryRef = doc(db, "clinicalHistory", user.uid);
        const clinicalHistoryDoc = await getDoc(clinicalHistoryRef);
        if (clinicalHistoryDoc.exists()) {
          setHasClinicalHistory(true);
          setClinicalHistoryData(clinicalHistoryDoc.data());
        } else {
          setHasClinicalHistory(false);
        }

        // 3. Dieta
        const dietPlanRef = doc(db, "dietPlans", user.uid);
        const dietPlanDoc = await getDoc(dietPlanRef);
        if (dietPlanDoc.exists()) {
          const fileName = dietPlanDoc.data().fileName;
          const dietStorageRef = ref(storage, `dietas/${user.uid}/${fileName}`);
          try {
            const url = await getDownloadURL(dietStorageRef);
            setDietPlanUrl(url);
          } catch (error) {
            console.warn("No se encontró la dieta en el almacenamiento.");
            setDietPlanUrl(null);
          }
        } else {
          setDietPlanUrl(null);
        }

        // 4. Rutina
        const routineRef = doc(db, "rutines", user.uid);
        const routineDoc = await getDoc(routineRef);
        if (routineDoc.exists()) {
          setRoutineData(routineDoc.data());
        }

        // 5. Posts
        const postsSnap = await getDocs(collection(db, "post"));
        const postsList = postsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        // Ordenar posts del más reciente al más antiguo
        postsList.sort((a, b) => {
          const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
          const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
          return dateB - dateA;
        });
        setPosts(postsList);

        // 6. Suscripción a notificaciones
        await subscribeToNotifications(user.uid, userDoc.data().name);

        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth, navigate, db, storage, subscribeToNotifications]);

  // Actualizar medidas del historial
  const handleUpdateMeasures = async (field, value) => {
    if (auth.currentUser) {
      try {
        const clinicalHistoryRef = doc(
          db,
          "clinicalHistory",
          auth.currentUser.uid
        );
        const updatedData = {
          ...clinicalHistoryData,
          [field]: value,
        };
        await setDoc(clinicalHistoryRef, updatedData);
        setClinicalHistoryData(updatedData);
        alert("Medidas actualizadas con éxito.");
      } catch (error) {
        console.error("Error al actualizar las medidas:", error);
        alert("Hubo un error al actualizar las medidas. Intenta de nuevo.");
      }
    }
  };

  const handleViewChange = (view) => setCurrentView(view);

  const getRoutinePlan = () => routineData?.plan || {};

  const getRoutineDaysWithExercises = () =>
    Object.entries(getRoutinePlan()).filter(
      ([, exercises]) => exercises && Object.keys(exercises).length > 0
    );

  const getTodayRoutineKey = () => {
    const raw = moment()
      .format("dddd")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return daysMap[raw];
  };

  const formatDayLabel = (day) => {
    if (!day) return "";
    const normalizedDay = day
      .replace("miercoles", "miércoles")
      .replace("sabado", "sábado");
    return normalizedDay.charAt(0).toUpperCase() + normalizedDay.slice(1);
  };

  // ============= RENDER SECCIONES =============

  // 1) HOME
  const renderHomeContent = () => {
    if (!hasClinicalHistory) {
      return (
        <div className="no-data-message">
          <h2>Bienvenido {userData?.name || "Usuario"}</h2>
          <p>
            Aún no tienes tu historial clínico, llénalo para poder recibir tu
            dieta y rutina.
          </p>
          <Button
            type="primary"
            onClick={() => handleViewChange("clinicalHistory")}
          >
            Ir al Historial Clínico
          </Button>
        </div>
      );
    }

    if (!dietPlanUrl && !routineData) {
      return (
        <div className="no-data-message">
          <h2>Bienvenido {userData?.name || "Usuario"}</h2>
          <p>
            Tu historial clínico ha sido enviado y estamos preparando tu dieta y
            rutina.
          </p>
        </div>
      );
    }

    const top3Posts = posts.slice(0, 3);
    const routineDays = getRoutineDaysWithExercises();
    const todayRoutineKey = getTodayRoutineKey();
    const hasTodayRoutine = Boolean(routineData?.plan?.[todayRoutineKey]);

    return (
      <div className="dashboard-overview">
        <div className="dashboard-hero">
          <img
            src="/assets/images/banner_dashboard.png"
            alt="Bienvenido a Mentfull"
          />
          <div className="dashboard-hero-copy">
            <span>Tu espacio Mentfull</span>
            <h1>Hola, {userData?.name || "Usuario"}</h1>
            <p>
              Consulta tu plan, revisa tu rutina del día y mantén tu avance a
              la mano.
            </p>
          </div>
        </div>

        <div className="dashboard-status-row">
          <div className="status-card">
            <span>Dieta</span>
            <strong>{dietPlanUrl ? "Disponible" : "En preparación"}</strong>
          </div>
          <div className="status-card">
            <span>Rutina</span>
            <strong>
              {routineData ? `${routineDays.length} días activos` : "En preparación"}
            </strong>
          </div>
          <div className="status-card">
            <span>Hoy</span>
            <strong>{hasTodayRoutine ? "Tienes entrenamiento" : "Sin rutina asignada"}</strong>
          </div>
        </div>

        <div className="shortcuts-section">
          {/* Atajo Dieta */}
          {dietPlanUrl && (
            <div className="diet-section">
              <div
                className="dashboard-shortcut green-shortcut"
                onClick={() => setIsModalVisible(true)}
              >
                <div className="icon-shortcut">
                  <img src="/assets/images/dietas_ico.png" alt="Dieta" />
                </div>
                <h2>Tu Dieta</h2>
              </div>
              <Modal
                title="Tu Plan Alimenticio"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
              >
                <iframe
                  src={dietPlanUrl}
                  title="Plan Alimenticio"
                  style={{ width: "100%", height: "600px" }}
                ></iframe>
              </Modal>
            </div>
          )}

          {/* Atajo Rutina */}
          {routineData ? (
            <div className="routine-section">
              <div
                className="dashboard-shortcut blue-shortcut"
                onClick={() => {
                  const dayKey = getTodayRoutineKey();
                  if (routineData.plan[dayKey]) {
                    setSelectedDay(dayKey);
                    handleViewChange("routineGrid");
                  } else {
                    handleViewChange("routines");
                  }
                }}
              >
                <p className="date-pill">
                  {moment().format("dddd, D [de] MMMM")}
                </p>
                <div className="icon-shortcut">
                  <img
                    src="/assets/images/calendario_ico.png"
                    alt="Calendario"
                  />
                </div>
                <h2>Tu Rutina</h2>
                <Button
                  type="link"
                  className="green-pill"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewChange("routines");
                  }}
                >
                  Ver Calendario
                </Button>
              </div>
            </div>
          ) : (
            <p>Aún no tenemos tu rutina lista.</p>
          )}

          {/* Atajo Medidas */}
          {routineData && dietPlanUrl ? (
            <div
              className="dashboard-shortcut gray-shortcut"
              onClick={() => {
                handleViewChange("clinicalHistory");
              }}
            >
              <div className="icon-shortcut">
                <img src="/assets/images/medidas_ico_1.png" alt="Medidas" />
              </div>
              <h2>Tus Medidas</h2>
            </div>
          ) : null}

          {/* Atajo Acompañamiento */}
          {routineData && dietPlanUrl ? (
            <div
              className="dashboard-shortcut pink-shortcut"
              onClick={() => {
                handleViewChange("acompanamiento");
              }}
            >
              <div className="icon-shortcut">
                <img
                  src="/assets/images/calorias_ico.png"
                  alt="Acompañamiento"
                />
              </div>
              <h2>Acompañamiento</h2>
            </div>
          ) : null}
        </div>

        {/* Posts recientes */}
        <div className="acompanamiento-shortcut">
          <div className="nav-acompanamiento">
            <Button
              className="green-breadcrumb"
              onClick={() => handleViewChange("acompanamiento")}
            >
              Ver todos
            </Button>
            <h3>Últimos posts</h3>
          </div>
          <div className="post-scroll">
            {top3Posts.length === 0 ? (
              <p>No hay publicaciones todavía.</p>
            ) : (
              top3Posts.map((post) => (
                <div className="post-card" key={post.id}>
                  <div className="post-header">
                    <div className="post-title">{post.title}</div>
                    <div className="post-date">
                      {post.createdAt
                        ? moment(post.createdAt.toDate()).format(
                            "DD/MM/YYYY HH:mm"
                          )
                        : ""}
                    </div>
                  </div>
                  <div className="post-body">{post.description}</div>
                  {post.header && (
                    <div className="post-media">
                      {isVideoUrl(post.header) ? (
                        <video src={post.header} controls />
                      ) : (
                        <img src={post.header} alt="header" />
                      )}
                    </div>
                  )}
                  {post.cta && post.ctaLink && (
                    <div className="post-footer">
                      <button
                        className="cta-button"
                        onClick={() => window.open(post.ctaLink, "_blank")}
                      >
                        {post.cta}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // 2) RUTINAS
  const renderRoutines = () => {
    if (!hasClinicalHistory) {
      return (
        <div className="no-data-message">
          <h2>Bienvenido {userData?.name || "Usuario"}</h2>
          <p>
            Aún no tienes tu historial clínico, llénalo para poder recibir tu
            rutina.
          </p>
          <Button
            type="primary"
            onClick={() => handleViewChange("clinicalHistory")}
          >
            Ir al Historial Clínico
          </Button>
        </div>
      );
    }
    if (!routineData) {
      return (
        <div className="no-data-message">
          <h2>Bienvenido {userData?.name || "Usuario"}</h2>
          <p>
            Tu historial clínico ha sido enviado y seguimos preparando tu
            rutina.
          </p>
        </div>
      );
    }
    const routineDays = getRoutineDaysWithExercises();
    return (
      <div className="dashboard-overview routines-overview">
        <div className="routine-section routine-calendar-section">
          <div className="routine-header">
            <div>
              <span>Calendario</span>
              <h3>Tu rutina semanal</h3>
              <p>
                Los días marcados tienen ejercicios asignados. Selecciona una
                fecha con rutina para ver el detalle completo.
              </p>
            </div>
            <div className="routine-summary">
              <strong>{routineDays.length}</strong>
              <span>días activos</span>
            </div>
          </div>
          <Calendar
            fullscreen={false}
            cellRender={(date) => {
              const dayMoment = moment(date.valueOf()).locale("es");
              const dayRaw = dayMoment
                .format("dddd")
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");
              const dayKey = daysMap[dayRaw];
              return routineData.plan[dayKey] ? (
                <div className="calendar-marker">
                  {Object.keys(routineData.plan[dayKey]).length} ejercicios
                </div>
              ) : null;
            }}
            onSelect={(date) => {
              const dayMoment = moment(date.valueOf()).locale("es");
              const dayRaw = dayMoment
                .format("dddd")
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");
              const dayKey = daysMap[dayRaw];
              if (routineData.plan[dayKey]) {
                setSelectedDay(dayKey);
                handleViewChange("routineGrid");
              }
            }}
          />
          <div className="routine-legend">
            <span className="legend-dot" />
            Días con entrenamiento disponible
          </div>
        </div>
      </div>
    );
  };

  // 3) DIETA
  const renderDiet = () => {
    if (!hasClinicalHistory) {
      return (
        <div className="no-data-message">
          <h2>Bienvenido {userData?.name || "Usuario"}</h2>
          <p>
            Aún no tienes tu historial clínico, llénalo para poder recibir tu
            dieta.
          </p>
          <Button
            type="primary"
            onClick={() => handleViewChange("clinicalHistory")}
          >
            Ir al Historial Clínico
          </Button>
        </div>
      );
    }

    if (!dietPlanUrl) {
      return (
        <div className="no-data-message">
          <h2>Bienvenido {userData?.name || "Usuario"}</h2>
          <p>
            Tu historial clínico ha sido enviado y seguimos preparando tu dieta.
          </p>
        </div>
      );
    }

    return (
      <div className="dashboard-overview diet-overview">
        <section className="diet-section diet-experience">
          <div className="diet-hero-card">
            <div className="diet-hero-copy">
              <span>Plan alimenticio</span>
              <h3>Tu dieta está lista</h3>
              <p>
                Un plan preparado para acompañarte con claridad, constancia y
                flexibilidad en tu día a día.
              </p>
              <Button
                type="primary"
                className="view-diet-button"
                onClick={() => setIsModalVisible(true)}
              >
                Ver Plan Alimenticio
              </Button>
            </div>
            <div className="diet-visual">
              <img
                src="/assets/images/dietas_ico.png"
                alt="Plan alimenticio"
                className="diet-icon-image"
              />
            </div>
          </div>
          <div className="diet-support-grid">
            <div>
              <strong>Consúltala cuando quieras</strong>
              <span>Tu PDF queda disponible en esta sección.</span>
            </div>
            <div>
              <strong>Avanza a tu ritmo</strong>
              <span>Úsala como guía diaria, sin complicarte.</span>
            </div>
            <div>
              <strong>Acompañamiento activo</strong>
              <span>Revisa el feed para tips y recordatorios.</span>
            </div>
          </div>
          <Modal
            title="Tu Plan Alimenticio"
            open={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            footer={null}
          >
            <iframe
              src={dietPlanUrl}
              title="Plan Alimenticio"
              style={{ width: "100%", height: "600px" }}
            ></iframe>
          </Modal>
        </section>
      </div>
    );
  };

  // 4) HISTORIAL CLÍNICO
  const renderClinicalHistory = () => {
    if (!hasClinicalHistory) {
      // Al completar el historial, se recarga la página para actualizar el estado y evitar doble envío.
      return (
        <ClinicalHistoryWizard onComplete={() => window.location.reload()} />
      );
    }

    const measures = [
      {
        key: "peso",
        label: "Peso",
        unit: "kg",
        icon: "/assets/images/calorias_ico.png",
      },
      {
        key: "cintura",
        label: "Cintura",
        unit: "cm",
        icon: "/assets/images/medidas_ico_1.png",
      },
      {
        key: "cadera",
        label: "Cadera",
        unit: "cm",
        icon: "/assets/images/medidas_ico_1.png",
      },
      {
        key: "pierna",
        label: "Pierna",
        unit: "cm",
        icon: "/assets/images/medidas_ico_1.png",
      },
      {
        key: "brazo",
        label: "Brazo",
        unit: "cm",
        icon: "/assets/images/medidas_ico_1.png",
      },
    ];

    return (
      <div className="measurements-view">
        <div className="measurements-header">
          <div>
            <span>Seguimiento corporal</span>
            <h2>Mis Medidas</h2>
            <p>
              Actualiza tus datos cuando tengas una nueva medición para mantener
              tu seguimiento al día.
            </p>
          </div>
        </div>

        <div className="measurements-grid">
          {measures.map((measure) => {
            const value = clinicalHistoryData?.[measure.key];
            return (
              <article className="measurement-card" key={measure.key}>
                <div className="measurement-icon">
                  <img src={measure.icon} alt="" />
                </div>
                <div className="measurement-content">
                  <span>{measure.label}</span>
                  {editingFields[measure.key] ? (
                    <Input
                      defaultValue={value}
                      autoFocus
                      suffix={measure.unit}
                      onBlur={(e) =>
                        handleBlurUpdate(measure.key, e.target.value)
                      }
                      onPressEnter={(e) =>
                        handleBlurUpdate(measure.key, e.target.value)
                      }
                    />
                  ) : (
                    <strong>
                      {value || "Sin dato"}{" "}
                      {value ? <small>{measure.unit}</small> : null}
                    </strong>
                  )}
                </div>
                {!editingFields[measure.key] && (
                  <Button type="link" onClick={() => handleEdit(measure.key)}>
                    Editar
                  </Button>
                )}
              </article>
            );
          })}
        </div>
      </div>
    );
  };

  // 5) RUTINE GRID (Vista detallada de ejercicios)
  const renderRoutineGrid = () => {
    if (!routineData) return null;
    const exercises = routineData.plan?.[selectedDay] || {};
    return (
      <div className="exercise-grid-view">
        <div className="breadcrumb">
          <Button
            className="green-breadcrumb"
            onClick={() => handleViewChange("routines")}
          >
            Regresar al calendario
          </Button>
        </div>
        <div className="exercise-day-header">
          <div>
            <span>Entrenamiento</span>
            <h3>Ejercicios para {formatDayLabel(selectedDay)}</h3>
            <p>
              Revisa series, repeticiones, descanso y abre los videos de apoyo
              antes de empezar cada movimiento.
            </p>
          </div>
          <div className="routine-summary">
            <strong>{Object.keys(exercises).length}</strong>
            <span>ejercicios</span>
          </div>
        </div>
        <div className="exercise-grid">
          {Object.entries(exercises).map(
            ([key, exercise], index) => (
              <div key={key} className="exercise-card">
                <div className="exercise-card-header">
                  <span>Ejercicio {index + 1}</span>
                  <h4>{exercise.descripcion || key}</h4>
                </div>
                <div className="exercise-metrics">
                  <div>
                    <span>Series</span>
                    <strong>{exercise.nSet}</strong>
                  </div>
                  <div>
                    <span>Reps</span>
                    <strong>{exercise.nRepeticiones}</strong>
                  </div>
                  <div>
                    <span>Peso</span>
                    <strong>{exercise.peso} lbs</strong>
                  </div>
                  <div>
                    <span>Descanso</span>
                    <strong>{exercise.descanso}s</strong>
                  </div>
                </div>
                {exercise.videos && exercise.videos.length > 0 && (
                  <div className="videos-buttons-group">
                    {exercise.videos.map((videoUrl, index) => (
                      <Button
                        key={index}
                        type="primary"
                        onClick={() => openVideoModal(videoUrl)}
                        style={{ marginRight: 8, marginBottom: 8 }}
                      >
                        Ver video ejemplo {index + 1}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )
          )}
        </div>

        <Modal
          open={isVideoModalVisible}
          onCancel={closeVideoModal}
          footer={null}
          title="Reproductor de Video"
        >
          {selectedVideoUrl && (
            <iframe
              src={selectedVideoUrl}
              title="Video"
              width="100%"
              height="400px"
              style={{ border: "none" }}
              allowFullScreen
            ></iframe>
          )}
        </Modal>
      </div>
    );
  };

  // 6) ACOMPAÑAMIENTO (Posts)
  const renderAcompanamiento = () => {
    return (
      <div className="acompanamiento-section feed-section">
        <div className="feed-header">
          <div>
            <span>Acompañamiento</span>
            <h2>Tu feed Mentfull</h2>
            <p>
              Publicaciones, recordatorios y recursos para sostener tu proceso
              entre consultas.
            </p>
          </div>
        </div>
        {posts.length === 0 ? (
          <div className="feed-empty">
            <h3>No hay publicaciones todavía.</h3>
            <p>Cuando tu equipo publique contenido, aparecerá aquí.</p>
          </div>
        ) : (
          <div className="feed-list">
            {posts.map((post) => (
            <article className="post-card feed-post-card" key={post.id}>
              <div className="post-header">
                <div className="post-author">
                  <div className="post-avatar">
                    <img src="/assets/images/logo-color.png" alt="Mentfull" />
                  </div>
                  <div>
                    <div className="post-title">{post.title}</div>
                    <div className="post-date">
                      {post.createdAt
                        ? moment(post.createdAt.toDate()).format(
                            "D [de] MMMM, HH:mm"
                          )
                        : "Mentfull"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="post-body">{post.description}</div>
              {post.header && (
                <div className="post-media">
                  {isVideoUrl(post.header) ? (
                    <video src={post.header} controls />
                  ) : (
                    <img src={post.header} alt="header" />
                  )}
                </div>
              )}
              {post.cta && post.ctaLink && (
                <div className="post-footer">
                  <button
                    className="cta-button"
                    onClick={() => window.open(post.ctaLink, "_blank")}
                  >
                    {post.cta}
                  </button>
                </div>
              )}
            </article>
            ))}
          </div>
        )}
      </div>
    );
  };

  // 7) AJUSTES DE CUENTA
  const renderAccountSettings = () => {
    const handleCancelSubscription = async () => {
      if (!userData?.subscriptionId) {
        alert("No tienes una suscripción activa para cancelar.");
        return;
      }

      if (
        !window.confirm(
          "¿Estás seguro de que deseas cancelar tu suscripción? Esta acción no se puede deshacer."
        )
      ) {
        return;
      }

      try {
        const response = await fetch(
          `${runtimeConfig.apiBaseUrl}/cancel-subscription`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ subscriptionId: userData.subscriptionId }),
          }
        );
        const data = await response.json();
        if (response.ok) {
          alert("Suscripción cancelada exitosamente.");
          // Recarga la página para actualizar la información del usuario
          window.location.reload();
        } else {
          alert("Error al cancelar la suscripción: " + (data.error || ""));
        }
      } catch (error) {
        console.error("Error al cancelar la suscripción:", error);
        alert(
          "Error al cancelar la suscripción. Por favor, inténtalo de nuevo."
        );
      }
    };

    return (
      <div className="account-settings-section">
        <h2>Ajustes de Cuenta</h2>

        <div className="account-card">
          <h3>Información Personal</h3>
          <p>
            <strong>Nombre:</strong> {userData?.name}
          </p>
          <p>
            <strong>Email:</strong> {userData?.email}
          </p>
          <p>
            <strong>Teléfono:</strong> {userData?.phone}
          </p>
          <p>
            <strong>Fecha de Nacimiento:</strong> {userData?.birthDate}
          </p>
          <p>
            <strong>Género:</strong> {userData?.gender}
          </p>
        </div>

        <div className="subscription-card">
          <h3>Suscripción</h3>
          {userData?.subscriptionId ? (
            <>
              <p>
                <strong>ID de Suscripción:</strong> {userData.subscriptionId}
              </p>
              <p>
                <strong>Plan Activo:</strong>{" "}
                {userData.subscriptionPlan || "N/A"}
              </p>
              <p>
                <strong>Costo Mensual:</strong>{" "}
                {userData.subscriptionCost
                  ? `$${userData.subscriptionCost} MXN`
                  : "N/A"}
              </p>
              <p>
                <strong>Inicio de Suscripción:</strong>{" "}
                {userData.subscriptionStartDate
                  ? moment(userData.subscriptionStartDate.toDate()).format(
                      "DD/MM/YYYY"
                    )
                  : "N/A"}
              </p>
              <p>
                <strong>Próxima Renovación:</strong>{" "}
                {userData.subscriptionRenewalDate
                  ? moment(userData.subscriptionRenewalDate.toDate()).format(
                      "DD/MM/YYYY"
                    )
                  : "N/A"}
              </p>
              <Button type="primary" danger onClick={handleCancelSubscription}>
                Cancelar Suscripción
              </Button>
            </>
          ) : (
            <p>No tienes ninguna suscripción activa.</p>
          )}
        </div>
      </div>
    );
  };

  // ============= SWITCH VISTAS =============
  const renderContent = () => {
    switch (currentView) {
      case "clinicalHistory":
        return renderClinicalHistory();
      case "home":
        return renderHomeContent();
      case "routines":
        return renderRoutines();
      case "diets":
        return renderDiet();
      case "routineGrid":
        return renderRoutineGrid();
      case "acompanamiento":
        return renderAcompanamiento();
      case "accountSettings":
        return renderAccountSettings();
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container">
      <NavbarD setView={handleViewChange} activeView={currentView} />
      <div className="main-content">
        {isLoading ? <Loader /> : renderContent()}
      </div>
    </div>
  );
};

// Detectar si es video
function isVideoUrl(url) {
  const ext = url.split(".").pop().toLowerCase();
  return ["mp4", "mov", "webm"].includes(ext);
}

export default Dashboard;
